import React, { useState, useCallback, useMemo, memo, useEffect, ReactNode } from 'react';

// Type definitions
interface HelmetProps {
  children: React.ReactElement;
}

interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

interface ConversionOptions {
  outputFormat: OutputFormat;
  extractionMode: ExtractionMode;
  pageRange: string;
  preserveLayout: boolean;
  includeHeaders: boolean;
  includeFooters: boolean;
  includePageNumbers: boolean;
  removeHyphenation: boolean;
  mergeParagraphs: boolean;
  extractTables: boolean;
  tableFormat: TableFormat;
  ocrEnabled: boolean;
  ocrLanguage: string;
  removeWatermarks: boolean;
  textCleanup: boolean;
  lineBreakHandling: LineBreakHandling;
  encoding: string;
  languageDetection: boolean;
  confidenceThreshold: number;
  imageDescriptions: boolean;
  metadataExtraction: boolean;
  structuredOutput: boolean;
  chunkSize: number;
  overlapSize: number;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

interface AnalyticsHook {
  track: (event: string, properties?: Record<string, any>) => void;
}

interface PDFInfo {
  fileName: string;
  fileSize: number;
  pageCount: number;
  textLength: number;
  language: string;
  hasImages: boolean;
  hasTables: boolean;
  isPasswordProtected: boolean;
  estimatedWords: number;
  metadata: {
    title?: string;
    author?: string;
    subject?: string;
    keywords?: string;
    creator?: string;
    producer?: string;
    creationDate?: string;
    modificationDate?: string;
  };
}

interface ExtractionResult {
  text: string;
  wordCount: number;
  characterCount: number;
  pageBreaks: number[];
  confidence: number;
  language: string;
  extractionTime: number;
}

// Output format options
type OutputFormat = 'txt' | 'rtf' | 'markdown' | 'csv' | 'json-text';
type ExtractionMode = 'standard' | 'layout-aware' | 'reading-order' | 'column-detection' | 'ai-enhanced';
type TableFormat = 'csv' | 'tabs' | 'spaces' | 'markdown' | 'json';
type LineBreakHandling = 'preserve' | 'normalize' | 'remove' | 'paragraph-breaks';

// Mock react-helmet-async for SEO
const Helmet: React.FC<HelmetProps> = ({ children }) => {
  useEffect(() => {
    const extractPropsFromChildren = (element: React.ReactElement): any => {
      if (element.props?.children) {
        const childArray = React.Children.toArray(element.props.children);
        const helmetData: any = { meta: [], link: [] };

        childArray.forEach((child: any) => {
          if (React.isValidElement(child)) {
            const childElement = child as React.ReactElement<any>;

            if (childElement.type === 'title') {
              helmetData.title = childElement.props.children;
            } else if (childElement.type === 'meta') {
              helmetData.meta.push(childElement.props);
            } else if (childElement.type === 'link') {
              helmetData.link.push(childElement.props);
            }
          }
        });
        return helmetData;
      }
      return {};
    };

    const helmetData = extractPropsFromChildren(children);

    if (helmetData.title) {
      document.title = helmetData.title;
    }

    const updateMeta = (name: string, content: string): void => {
      let meta = document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (helmetData.meta && Array.isArray(helmetData.meta)) {
      helmetData.meta.forEach((meta: any) => {
        if (meta.name) updateMeta(meta.name, meta.content);
        if (meta.property) updateMeta(meta.property, meta.content);
      });
    }
  }, [children]);

  return null;
};

// Analytics hook
const useAnalytics = (): AnalyticsHook => {
  const track = useCallback((event: string, properties: Record<string, any> = {}): void => {
    console.log('Analytics Event:', event, properties);
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, properties);
    }
  }, []);

  return { track };
};

// Theme context
const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('pdf-text-theme');
      return stored === 'dark' ||
        (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  const toggleTheme = useCallback((): void => {
    setIsDark((prev) => {
      const newTheme = !prev;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('pdf-text-theme', newTheme ? 'dark' : 'light');
      }
      return newTheme;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Mock PDF analysis function
const analyzePDFFile = async (file: File): Promise<PDFInfo> => {
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  const mockPageCount = Math.floor(Math.random() * 20) + 1;
  const mockTextLength = Math.floor(Math.random() * 50000) + 1000;
  
  return {
    fileName: file.name,
    fileSize: file.size,
    pageCount: mockPageCount,
    textLength: mockTextLength,
    language: 'English',
    hasImages: Math.random() > 0.4,
    hasTables: Math.random() > 0.6,
    isPasswordProtected: Math.random() > 0.9,
    estimatedWords: Math.floor(mockTextLength / 5),
    metadata: {
      title: 'Sample Document',
      author: 'Document Author',
      subject: 'PDF Document',
      keywords: 'text, extraction, pdf',
      creator: 'PDF Creator',
      producer: 'PDF Producer',
      creationDate: '2024-01-15',
      modificationDate: new Date().toISOString().split('T')[0]
    }
  };
};

// Mock PDF to Text conversion
const convertPDFToText = async (file: File, options: ConversionOptions): Promise<{ blob: Blob; result: ExtractionResult }> => {
  await new Promise(resolve => setTimeout(resolve, 2500));
  
  // Generate mock text content
  const mockText = `SAMPLE EXTRACTED TEXT
====================

This is a sample of text extracted from your PDF document. The actual conversion would extract all readable text content from the PDF file.

Chapter 1: Introduction
-----------------------
Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.

Key Features:
• High-quality text extraction
• Layout preservation options
• OCR support for scanned documents
• Multiple output formats
• Advanced formatting options

Chapter 2: Data Analysis
------------------------
The following table shows sample data:

Product     | Sales  | Revenue
-----------|--------|----------
Widget A   | 1,500  | $75,000
Widget B   | 1,200  | $60,000
Widget C   | 800    | $40,000

Summary Statistics:
- Total Products: 3
- Total Sales: 3,500 units
- Total Revenue: $175,000
- Average Price: $50 per unit

Conclusion
----------
This demonstrates the text extraction capabilities including tables, lists, headers, and formatted content. The actual output would contain all text from your PDF document.

---
Document extracted on ${new Date().toLocaleDateString()}
Total pages processed: ${Math.floor(Math.random() * 10) + 1}
Extraction confidence: ${Math.floor(Math.random() * 20) + 80}%`;

  const result: ExtractionResult = {
    text: mockText,
    wordCount: mockText.split(/\s+/).length,
    characterCount: mockText.length,
    pageBreaks: [0, 500, 1200],
    confidence: Math.floor(Math.random() * 20) + 80,
    language: 'English',
    extractionTime: Math.floor(Math.random() * 3000) + 1000
  };

  let outputText = mockText;
  
  // Apply formatting based on options
  if (options.outputFormat === 'markdown') {
    outputText = mockText; // Already in markdown-like format
  } else if (options.outputFormat === 'csv' && options.extractTables) {
    outputText = `Product,Sales,Revenue
Widget A,1500,75000
Widget B,1200,60000
Widget C,800,40000`;
  } else if (options.outputFormat === 'json-text') {
    outputText = JSON.stringify({
      content: mockText,
      metadata: {
        wordCount: result.wordCount,
        characterCount: result.characterCount,
        confidence: result.confidence,
        language: result.language
      }
    }, null, 2);
  }

  const mimeType = options.outputFormat === 'json-text' ? 'application/json' : 'text/plain';
  const blob = new Blob([outputText], { type: mimeType });

  return { blob, result };
};

// Header Component
const Header: React.FC = memo(() => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className={`border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
      <div className="max-w-6xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-gradient-to-br from-blue-500 to-purple-600' : 'bg-gradient-to-br from-blue-500 to-indigo-600'}`}>
              <span className="text-white font-bold text-lg">📄</span>
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                PDF Text Extractor
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Extract and convert PDF text with precision
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              title="Toggle Theme"
            >
              {isDark ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

Header.displayName = 'Header';

// Stats Card Component
const StatsCard: React.FC<{ icon: string; title: string; value: string; color: string; isDark: boolean }> = memo(({ icon, title, value, color, isDark }) => (
  <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} hover:shadow-lg transition-shadow`}>
    <div className="flex items-center justify-between">
      <div>
        <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{title}</p>
        <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
        <span className="text-2xl">{icon}</span>
      </div>
    </div>
  </div>
));

StatsCard.displayName = 'StatsCard';

// Progress Ring Component
const ProgressRing: React.FC<{ progress: number; size: number; strokeWidth: number; isDark: boolean }> = memo(({ progress, size, strokeWidth, isDark }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={isDark ? '#374151' : '#e5e7eb'}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="url(#gradient)"
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-300"
        />
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
});

ProgressRing.displayName = 'ProgressRing';

// File Upload Component
const FileUpload: React.FC<{
  onFileSelect: (file: File) => void;
  isDark: boolean;
  isProcessing: boolean;
}> = memo(({ onFileSelect, isDark, isProcessing }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type === 'application/pdf') {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  return (
    <div className={`relative rounded-2xl border-2 border-dashed p-8 text-center transition-all ${
      dragActive 
        ? isDark ? 'border-blue-400 bg-blue-900/20' : 'border-blue-400 bg-blue-50'
        : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
    } ${isProcessing ? 'pointer-events-none opacity-50' : ''}`}
      onDrop={handleDrop}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={() => setDragActive(false)}
    >
      <div className="space-y-4">
        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
          isDark ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20' : 'bg-gradient-to-br from-blue-100 to-indigo-100'
        }`}>
          <span className="text-3xl">📄</span>
        </div>
        
        <div>
          <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Drop your PDF here
          </h3>
          <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Or click to browse files
          </p>
        </div>

        <input
          type="file"
          accept=".pdf"
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
          disabled={isProcessing}
        />
        
        <label
          htmlFor="file-upload"
          className={`inline-flex items-center px-6 py-3 rounded-xl font-medium cursor-pointer transition-all ${
            isDark 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
              : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
          } ${isProcessing ? 'cursor-not-allowed opacity-50' : 'hover:shadow-lg'}`}
        >
          <span className="mr-2">📁</span>
          Choose PDF File
        </label>

        <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          Maximum file size: 100MB • Supports password-protected PDFs
        </p>
      </div>
    </div>
  );
});

FileUpload.displayName = 'FileUpload';

// Text Preview Component
const TextPreview: React.FC<{ text: string; isDark: boolean }> = memo(({ text, isDark }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 1000;
  const isLong = text.length > maxLength;
  const displayText = isExpanded ? text : text.slice(0, maxLength);

  return (
    <div className={`rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            📝 Extracted Text Preview
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => navigator.clipboard.writeText(text)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              📋 Copy
            </button>
            {isLong && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                {isExpanded ? 'Collapse' : 'Expand'}
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="p-4">
        <pre className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          {displayText}
          {isLong && !isExpanded && (
            <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'}`}>...</span>
          )}
        </pre>
        
        {isLong && (
          <div className={`mt-4 pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {text.length.toLocaleString()} characters • {text.split('\n').length} lines • {text.split(/\s+/).length} words
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

TextPreview.displayName = 'TextPreview';

// Main Component
const PDFToTextConverter: React.FC = () => {
  const { isDark } = useTheme();
  const { track } = useAnalytics();

  // States
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pdfInfo, setPdfInfo] = useState<PDFInfo | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [extractedText, setExtractedText] = useState<string>('');
  const [extractionResult, setExtractionResult] = useState<ExtractionResult | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Options
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('txt');
  const [extractionMode, setExtractionMode] = useState<ExtractionMode>('standard');
  const [pageRange, setPageRange] = useState('all');
  const [preserveLayout, setPreserveLayout] = useState(false);
  const [ocrEnabled, setOcrEnabled] = useState(false);
  const [textCleanup, setTextCleanup] = useState(true);

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    setSelectedFile(file);
    setError('');
    setSuccess('');
    setExtractedText('');
    setExtractionResult(null);

    track('pdf_file_selected', { fileName: file.name, fileSize: file.size });

    setIsAnalyzing(true);
    try {
      const info = await analyzePDFFile(file);
      setPdfInfo(info);
      track('pdf_analyzed', { pageCount: info.pageCount, hasImages: info.hasImages });
    } catch (err) {
      setError('Failed to analyze PDF file');
      track('pdf_analysis_error', { error: String(err) });
    } finally {
      setIsAnalyzing(false);
    }
  }, [track]);

  // Handle conversion
  const handleConvert = useCallback(async () => {
    if (!selectedFile) return;

    setIsConverting(true);
    setError('');
    setSuccess('');
    setConversionProgress(0);

    const options: ConversionOptions = {
      outputFormat,
      extractionMode,
      pageRange,
      preserveLayout,
      includeHeaders: false,
      includeFooters: false,
      includePageNumbers: false,
      removeHyphenation: textCleanup,
      mergeParagraphs: textCleanup,
      extractTables: true,
      tableFormat: 'tabs',
      ocrEnabled,
      ocrLanguage: 'eng',
      removeWatermarks: false,
      textCleanup,
      lineBreakHandling: 'normalize',
      encoding: 'UTF-8',
      languageDetection: true,
      confidenceThreshold: 80,
      imageDescriptions: false,
      metadataExtraction: true,
      structuredOutput: false,
      chunkSize: 1000,
      overlapSize: 100
    };

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setConversionProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 200);

      const { blob, result } = await convertPDFToText(selectedFile, options);
      
      clearInterval(progressInterval);
      setConversionProgress(100);

      const text = await blob.text();
      setExtractedText(text);
      setExtractionResult(result);

      // Download file
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const extension = outputFormat === 'json-text' ? 'json' : outputFormat;
      link.download = `${selectedFile.name.replace('.pdf', '')}_extracted.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('Text extracted successfully! Download started automatically.');
      track('pdf_converted', { format: outputFormat, wordCount: result.wordCount });

    } catch (err) {
      setError('Failed to extract text from PDF');
      track('conversion_error', { error: String(err) });
    } finally {
      setIsConverting(false);
      setConversionProgress(0);
    }
  }, [selectedFile, outputFormat, extractionMode, pageRange, preserveLayout, ocrEnabled, textCleanup, track]);

  // Clear all
  const clearAll = useCallback(() => {
    setSelectedFile(null);
    setPdfInfo(null);
    setExtractedText('');
    setExtractionResult(null);
    setError('');
    setSuccess('');
    track('data_cleared');
  }, [track]);

  return (
    <div className={`min-h-screen transition-colors ${isDark ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'}`}>
      
      {/* SEO */}
      <Helmet>
        <>
          <title>PDF to Text Converter - Extract Text from PDF Files Online</title>
          <meta name="description" content="Convert PDF to text online for free. Extract text from PDF documents with OCR support, layout preservation, and multiple output formats." />
          <meta name="keywords" content="PDF to text, PDF converter, text extraction, OCR, online converter" />
        </>
      </Helmet>

      <Header />

      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatsCard
            icon="📄"
            title="Files Processed"
            value="2,847"
            color={isDark ? 'bg-blue-500/20' : 'bg-blue-100'}
            isDark={isDark}
          />
          <StatsCard
            icon="⚡"
            title="Avg Speed"
            value="< 30s"
            color={isDark ? 'bg-green-500/20' : 'bg-green-100'}
            isDark={isDark}
          />
          <StatsCard
            icon="🎯"
            title="Accuracy"
            value="98.5%"
            color={isDark ? 'bg-purple-500/20' : 'bg-purple-100'}
            isDark={isDark}
          />
          <StatsCard
            icon="🔒"
            title="Privacy"
            value="100%"
            color={isDark ? 'bg-orange-500/20' : 'bg-orange-100'}
            isDark={isDark}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Upload & Settings */}
          <div className="lg:col-span-1">
            <div className={`rounded-2xl p-6 ${isDark ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-gray-200'} shadow-xl`}>
              
              {/* File Upload */}
              <div className="mb-6">
                <FileUpload 
                  onFileSelect={handleFileSelect}
                  isDark={isDark}
                  isProcessing={isAnalyzing || isConverting}
                />
              </div>

              {/* File Info */}
              {selectedFile && (
                <div className={`p-4 rounded-xl mb-6 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <h3 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    📋 File Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Name:</span>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {selectedFile.name.length > 20 ? selectedFile.name.slice(0, 20) + '...' : selectedFile.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Size:</span>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    {pdfInfo && (
                      <>
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Pages:</span>
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {pdfInfo.pageCount}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Est. Words:</span>
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {pdfInfo.estimatedWords.toLocaleString()}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {isAnalyzing && (
                    <div className="mt-4 flex items-center">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2"></div>
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Analyzing PDF...
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Settings */}
              <div className="space-y-4">
                <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  ⚙️ Extraction Settings
                </h3>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Output Format
                  </label>
                  <select
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                    className={`w-full p-3 rounded-xl border transition-all ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    } focus:ring-2 focus:ring-blue-500/20`}
                  >
                    <option value="txt">Plain Text (.txt)</option>
                    <option value="rtf">Rich Text (.rtf)</option>
                    <option value="markdown">Markdown (.md)</option>
                    <option value="csv">CSV for Tables (.csv)</option>
                    <option value="json-text">JSON Format (.json)</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Extraction Mode
                  </label>
                  <select
                    value={extractionMode}
                    onChange={(e) => setExtractionMode(e.target.value as ExtractionMode)}
                    className={`w-full p-3 rounded-xl border transition-all ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    } focus:ring-2 focus:ring-blue-500/20`}
                  >
                    <option value="standard">Standard Extraction</option>
                    <option value="layout-aware">Layout Aware</option>
                    <option value="reading-order">Reading Order</option>
                    <option value="column-detection">Column Detection</option>
                    <option value="ai-enhanced">AI Enhanced</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Page Range
                  </label>
                  <input
                    type="text"
                    value={pageRange}
                    onChange={(e) => setPageRange(e.target.value)}
                    placeholder="e.g., 1-5, 8, 10-12 or 'all'"
                    className={`w-full p-3 rounded-xl border transition-all ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                    } focus:ring-2 focus:ring-blue-500/20`}
                  />
                </div>

                {/* Options */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Preserve Layout
                    </label>
                    <button
                      onClick={() => setPreserveLayout(!preserveLayout)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        preserveLayout 
                          ? 'bg-gradient-to-r from-blue-500 to-purple-500' 
                          : isDark ? 'bg-gray-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preserveLayout ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Enable OCR
                    </label>
                    <button
                      onClick={() => setOcrEnabled(!ocrEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        ocrEnabled 
                          ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                          : isDark ? 'bg-gray-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        ocrEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Text Cleanup
                    </label>
                    <button
                      onClick={() => setTextCleanup(!textCleanup)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        textCleanup 
                          ? 'bg-gradient-to-r from-orange-500 to-red-500' 
                          : isDark ? 'bg-gray-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        textCleanup ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>

                {/* Convert Button */}
                <button
                  onClick={handleConvert}
                  disabled={!selectedFile || isConverting || isAnalyzing}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all ${
                    selectedFile && !isConverting && !isAnalyzing
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  {isConverting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Extracting Text...</span>
                    </div>
                  ) : (
                    <>🔄 Extract Text</>
                  )}
                </button>

                {/* Clear Button */}
                {selectedFile && (
                  <button
                    onClick={clearAll}
                    className={`w-full py-2 px-4 rounded-xl font-medium transition-all ${
                      isDark 
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                  >
                    🗑️ Clear All
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="lg:col-span-2">
            <div className={`rounded-2xl p-6 h-full ${isDark ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-gray-200'} shadow-xl`}>
              
              {/* Processing Status */}
              {isConverting && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      🔄 Processing PDF
                    </h3>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {Math.round(conversionProgress)}%
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-center mb-4">
                    <ProgressRing 
                      progress={conversionProgress} 
                      size={120} 
                      strokeWidth={8} 
                      isDark={isDark} 
                    />
                  </div>

                  <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                      style={{ width: `${conversionProgress}%` }}
                    />
                  </div>

                  <p className={`text-center mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Analyzing document structure and extracting text...
                  </p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className={`p-4 rounded-xl mb-6 border ${isDark ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                  <div className="flex items-center">
                    <span className="text-xl mr-2">❌</span>
                    <div>
                      <h4 className="font-medium">Extraction Failed</h4>
                      <p className="text-sm mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className={`p-4 rounded-xl mb-6 border ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'}`}>
                  <div className="flex items-center">
                    <span className="text-xl mr-2">✅</span>
                    <div>
                      <h4 className="font-medium">Extraction Complete</h4>
                      <p className="text-sm mt-1">{success}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Extraction Results */}
              {extractionResult && (
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    📊 Extraction Results
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className={`p-3 rounded-lg text-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className={`text-lg font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                        {extractionResult.wordCount.toLocaleString()}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Words</div>
                    </div>
                    <div className={`p-3 rounded-lg text-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                        {extractionResult.characterCount.toLocaleString()}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Characters</div>
                    </div>
                    <div className={`p-3 rounded-lg text-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className={`text-lg font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                        {extractionResult.confidence}%
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Confidence</div>
                    </div>
                    <div className={`p-3 rounded-lg text-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className={`text-lg font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                        {(extractionResult.extractionTime / 1000).toFixed(1)}s
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Time</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Text Preview */}
              {extractedText ? (
                <TextPreview text={extractedText} isDark={isDark} />
              ) : !selectedFile ? (
                <div className={`flex flex-col items-center justify-center h-96 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="text-6xl mb-4">📄</div>
                  <h3 className="text-xl font-medium mb-2">Ready to Extract Text</h3>
                  <p className="text-sm">Upload a PDF file to start text extraction</p>
                </div>
              ) : !isConverting && !extractedText ? (
                <div className={`flex flex-col items-center justify-center h-96 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="text-6xl mb-4">🔄</div>
                  <h3 className="text-xl font-medium mb-2">File Ready</h3>
                  <p className="text-sm">Click "Extract Text" to begin processing</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className={`mt-12 rounded-2xl p-8 ${isDark ? 'bg-gray-800/30 border border-gray-700' : 'bg-white/50 border border-gray-200'}`}>
          <h2 className={`text-2xl font-bold mb-6 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            🚀 Powerful Text Extraction Features
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>High Accuracy</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Advanced algorithms ensure 98.5% accuracy in text extraction
              </p>
            </div>

            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                <span className="text-2xl">👁️</span>
              </div>
              <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>OCR Support</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Extract text from scanned PDFs and image-based documents
              </p>
            </div>

            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                <span className="text-2xl">📐</span>
              </div>
              <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Layout Preservation</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Maintain original document formatting and structure
              </p>
            </div>

            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Fast Processing</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Extract text from most PDFs in under 30 seconds
              </p>
            </div>

            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`}>
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Privacy First</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                All processing happens locally - your files never leave your device
              </p>
            </div>

            <div className="text-center">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center ${isDark ? 'bg-teal-500/20' : 'bg-teal-100'}`}>
                <span className="text-2xl">📝</span>
              </div>
              <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Multiple Formats</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Export to TXT, RTF, Markdown, CSV, and JSON formats
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App with Provider
const PDFToText: React.FC = () => {
  return (
    <ThemeProvider>
      <PDFToTextConverter />
    </ThemeProvider>
  );
};

export default PDFToText;